import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  flatMap,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  ActionsEnum as FeaturesDataActionsEnum,
  fetchFeaturesData,
} from '../features-data/features-data.actions';
import {
  ActionsEnum,
  IResendVerificationEmail,
  IResendVerificationEmailError,
  IResendVerificationEmailSuccess,
  resendVerificationEmailError,
  resendVerificationEmailSuccess,
} from './verify-account.actions';
import { VerifyAccountService } from './verify-account.service';
import { DialogLoadingComponent, I2gDialogService } from 'i2g-ng-ui';
import { TranslateService } from '@ngx-translate/core';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import {
  FetchFeaturesData,
  FetchFeaturesDataSuccess,
} from '../features-data/features-data.actions';
import { v4 } from 'uuid';
import { FeaturesDataStateService } from '../features-data/features-data-state.service';

@Injectable()
export class VerifyAccountEffects {
  constructor(
    private _actions: Actions,
    private _verifyAccountService: VerifyAccountService,
    private _globalBannerService: GlobalBannerService,
    private _translateService: TranslateService,
    private _featuresDataStateService: FeaturesDataStateService,
    private _dialogService: I2gDialogService
  ) {}

  @Effect()
  onResendVerificationEmail: Observable<
    IResendVerificationEmailSuccess | IResendVerificationEmailError
  > = this._actions.pipe(
    ofType(ActionsEnum.RESEND_VERIFICATION_EMAIL),
    switchMap(({ payload }: IResendVerificationEmail) => {
      const { shouldOpenDialog } = payload;

      if (shouldOpenDialog) {
        this._dialogService.openDialog(DialogLoadingComponent);
      }

      return this._verifyAccountService.resendVerificationEmail().pipe(
        flatMap(() => of(resendVerificationEmailSuccess(shouldOpenDialog))),
        catchError(() => of(resendVerificationEmailError(shouldOpenDialog)))
      );
    })
  );

  @Effect({
    dispatch: false,
  })
  onResendVerificationEmailSuccess = this._actions.pipe(
    ofType(ActionsEnum.RESEND_VERIFICATION_EMAIL_SUCCESS),
    tap(({ payload }: IResendVerificationEmailSuccess) => {
      const { shouldOpenDialog } = payload;

      if (shouldOpenDialog) {
        this._verifyAccountService.openVerificationEmailSentDialog();
      }
    })
  );

  @Effect({
    dispatch: false,
  })
  onResendVerificationEmailError = this._actions.pipe(
    ofType(ActionsEnum.RESEND_VERIFICATION_EMAIL_ERROR),
    tap(({ payload }: IResendVerificationEmailError) => {
      const { shouldOpenDialog } = payload;

      if (shouldOpenDialog) {
        this._dialogService.closeDialog();
      }

      this._globalBannerService.showSystemErrorBanner(
        new Error('failed to resend verification email'),
        this._translateService.instant('ERROR.SERVER_ERROR')
      );
    })
  );

  @Effect()
  onFetchFeaturesDataSuccess: Observable<FetchFeaturesData> = this._actions.pipe(
    ofType(FeaturesDataActionsEnum.FETCH_FEATURES_DATA_SUCCESS),
    tap(() => this._verifyAccountService.clearVerifyAccountCookie()),
    filter(
      ({ payload }: FetchFeaturesDataSuccess) =>
        !this._featuresDataStateService.isAccountVerified(
          payload.featuresData.features
        )
    ),
    switchMap(() => this._verifyAccountService.didVerifyAccount()),
    map(() => fetchFeaturesData(v4()))
  );
}
